import { useAuth } from "@clerk/clerk-react";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";

import { useAccountReviewState } from "features/Auth/Onboarding/shared/AccountReviewUpsell";
import { fetcherAuth } from "utils/api";
import {
  DISMISSED_ACCOUNT_REVIEW_CARD,
  DISPLAYED_ACCOUNT_REVIEW_PURCHASE_DIALOG,
} from "utils/localStorage";

export enum OnboardingReviewRequestStatus {
  pending = "pending",
  fulfilled = "fulfilled",
}

interface OnboardingReviewRequest {
  id: number;
  status: OnboardingReviewRequestStatus;
  isArchived: boolean;
  planName: string;
  updatedAt: string;
}

export type OnboardingReviewState = "available" | "submitted" | "reviewed";

export interface OnboardingReviewCard {
  visible: boolean;
  state: OnboardingReviewState;
  fetchLoading: boolean;
  submitLoading: boolean;
  showPurchaseDialog: boolean;
  setShowPurchaseDialog: (open: boolean) => void;
  submitReview: () => Promise<void>;
  dismissCard: () => void;
}

const REVIEWED_CARD_TTL_MS = 14 * 24 * 60 * 60 * 1000;

export const useOnboardingReviewCard = (): OnboardingReviewCard => {
  const { getToken } = useAuth();
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { canSubmitReview } = useAccountReviewState();

  const [request, setRequest] = useState<OnboardingReviewRequest | null>(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const dismissCardKey = currentOrg?.id
    ? `${DISMISSED_ACCOUNT_REVIEW_CARD}-${currentOrg.id}`
    : null;

  useEffect(() => {
    if (dismissCardKey) {
      setDismissed(localStorage.getItem(dismissCardKey) === "true");
    }
  }, [dismissCardKey]);

  useEffect(() => {
    if (!currentOrg?.id || !canSubmitReview) {
      setFetchLoading(false);
      return;
    }
    const fetchRequest = async () => {
      setFetchLoading(true);
      try {
        const res = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/onboarding-review-requests`,
        );
        setRequest(res?.onboardingReviewRequest ?? null);
      } catch (error) {
        setErrorAlert(error);
      } finally {
        setFetchLoading(false);
      }
    };
    fetchRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, canSubmitReview]);

  useEffect(() => {
    if (!currentOrg?.id || !canSubmitReview || fetchLoading) return;
    const hasRequested = request && !request.isArchived;
    const dialogKey = `${DISPLAYED_ACCOUNT_REVIEW_PURCHASE_DIALOG}-${currentOrg.id}`;
    if (!hasRequested && localStorage.getItem(dialogKey) !== "true") {
      localStorage.setItem(dialogKey, "true");
      setShowPurchaseDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, canSubmitReview, fetchLoading, request]);

  const hasActiveRequest = request && !request.isArchived;
  const isFulfilled =
    hasActiveRequest &&
    request.status === OnboardingReviewRequestStatus.fulfilled;
  const isPending =
    hasActiveRequest &&
    request.status === OnboardingReviewRequestStatus.pending;
  const isReviewedExpired =
    isFulfilled &&
    Date.now() - new Date(request.updatedAt).getTime() > REVIEWED_CARD_TTL_MS;

  const visible =
    canSubmitReview && !(isFulfilled && (dismissed || isReviewedExpired));

  const state: OnboardingReviewState = isFulfilled
    ? "reviewed"
    : isPending
      ? "submitted"
      : "available";

  const dismissCard = () => {
    if (dismissCardKey) localStorage.setItem(dismissCardKey, "true");
    setDismissed(true);
  };

  const submitReview = async () => {
    if (!currentOrg?.id) return;
    setSubmitLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/onboarding-review-requests`,
        "POST",
      );
      setRequest(res?.onboardingReviewRequest ?? null);
      setAlert(
        "Review submitted! Our team will be in touch shortly.",
        "success",
      );
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSubmitLoading(false);
    }
  };

  return {
    visible,
    state,
    fetchLoading,
    submitLoading,
    showPurchaseDialog,
    setShowPurchaseDialog,
    submitReview,
    dismissCard,
  };
};
